import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Settings, Target, Calendar, Save, Lock, Snowflake } from 'lucide-react';
import { apiClient } from '../api/client';
import { changePassword } from '../api/auth';

interface GamificationData {
  xp: number;
  level: number;
  title: string;
  current_streak: number;
  best_streak: number;
  weekly_goal_days: number;
  daily_goal: number;
  streak_freezes: number;
}

export const SettingsPage: React.FC = () => {
  const [gamification, setGamification] = useState<GamificationData | null>(null);
  const [weeklyGoal, setWeeklyGoal] = useState(5);
  const [dailyGoal, setDailyGoal] = useState(20);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [pwSaving, setPwSaving] = useState(false);
  const [pwMessage, setPwMessage] = useState('');

  const handleChangePassword = async () => {
    setPwMessage('');
    if (newPw !== confirmPw) {
      setPwMessage('Failed: passwords do not match');
      return;
    }
    if (newPw.length < 8) {
      setPwMessage('Failed: new password must be at least 8 characters');
      return;
    }
    setPwSaving(true);
    try {
      await changePassword(currentPw, newPw);
      setPwMessage('Password updated');
      setCurrentPw('');
      setNewPw('');
      setConfirmPw('');
      setTimeout(() => setPwMessage(''), 3000);
    } catch {
      setPwMessage('Failed: current password is wrong');
    } finally {
      setPwSaving(false);
    }
  };

  useEffect(() => {
    apiClient.get<GamificationData>('/user/gamification')
      .then(({ data }) => {
        setGamification(data);
        setWeeklyGoal(data.weekly_goal_days);
        setDailyGoal(data.daily_goal);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setMessage('');
    try {
      const { data } = await apiClient.post<GamificationData>('/user/goal', {
        weekly_goal_days: weeklyGoal,
        daily_goal: dailyGoal,
      });
      setGamification(data);
      setMessage('Goals updated successfully');
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setMessage(err instanceof Error ? err.message : 'Failed to update goals');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="w-5 h-5 border-2 border-white/20 border-t-gold rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-xl mx-auto px-4 py-8 pb-24 md:pb-8">
      <div className="flex items-center gap-3 mb-8">
        <div className="w-10 h-10 rounded-xl bg-gold-glow border border-gold-dim flex items-center justify-center">
          <Settings size={20} className="text-gold" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-cream">Settings</h1>
          <p className="text-xs text-text-dim">Customize your learning goals</p>
        </div>
      </div>

      {message && (
        <motion.div
          initial={{ opacity: 0, y: -4 }}
          animate={{ opacity: 1, y: 0 }}
          className={`mb-4 px-4 py-3 rounded-lg text-sm font-medium ${
            message.includes('Failed') ? 'bg-red-muted text-red border border-red/20' : 'bg-green-muted text-green border border-green/20'
          }`}
        >
          {message}
        </motion.div>
      )}

      <div className="space-y-4">
        <div className="glass-strong rounded-2xl p-5 border border-border">
          <div className="flex items-center gap-2 mb-4">
            <Target size={16} className="text-gold" />
            <h2 className="text-sm font-semibold text-cream">Daily Goal</h2>
          </div>
          <p className="text-xs text-text-dim mb-3">How many words do you want to study each day?</p>
          <div className="flex items-center gap-3">
            <input
              type="range"
              min={5}
              max={100}
              step={5}
              value={dailyGoal}
              onChange={(e) => setDailyGoal(parseInt(e.target.value))}
              className="flex-1 accent-gold"
            />
            <input
              type="number"
              min={1}
              max={200}
              value={dailyGoal}
              onChange={(e) => setDailyGoal(parseInt(e.target.value) || 0)}
              className="w-20 bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2 text-sm text-cream outline-none focus:border-gold text-center"
            />
          </div>
        </div>

        <div className="glass-strong rounded-2xl p-5 border border-border">
          <div className="flex items-center gap-2 mb-4">
            <Calendar size={16} className="text-gold" />
            <h2 className="text-sm font-semibold text-cream">Weekly Goal</h2>
          </div>
          <p className="text-xs text-text-dim mb-3">How many days per week do you want to study?</p>
          <div className="flex items-center gap-3">
            <input
              type="range"
              min={1}
              max={7}
              step={1}
              value={weeklyGoal}
              onChange={(e) => setWeeklyGoal(parseInt(e.target.value))}
              className="flex-1 accent-gold"
            />
            <span className="w-20 text-center text-sm text-cream font-medium">{weeklyGoal} days</span>
          </div>
        </div>

        <button
          onClick={handleSave}
          disabled={saving}
          className="w-full py-3 rounded-xl btn-gold text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
        >
          {saving ? (
            <span className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" />
          ) : (
            <Save size={16} />
          )}
          Save Goals
        </button>

        {/* Streak freezes info */}
        <div className="glass-strong rounded-2xl p-5 border border-border">
          <div className="flex items-center gap-2 mb-2">
            <Snowflake size={16} className="text-blue" />
            <h2 className="text-sm font-semibold text-cream">Streak Freezes</h2>
          </div>
          <p className="text-xs text-text-dim leading-relaxed">
            You have <span className="text-blue font-semibold">{gamification?.streak_freezes ?? 0}</span> streak
            freeze{(gamification?.streak_freezes ?? 0) === 1 ? '' : 's'}. A freeze automatically covers one missed
            day so your streak survives. You earn one for every 7-day streak (max 2).
          </p>
        </div>

        {/* Change password */}
        <div className="glass-strong rounded-2xl p-5 border border-border">
          <div className="flex items-center gap-2 mb-4">
            <Lock size={16} className="text-gold" />
            <h2 className="text-sm font-semibold text-cream">Change Password</h2>
          </div>
          {pwMessage && (
            <p className={`mb-3 text-xs font-medium ${pwMessage.startsWith('Failed') ? 'text-red' : 'text-green'}`}>
              {pwMessage}
            </p>
          )}
          <div className="space-y-3">
            <input
              type="password"
              value={currentPw}
              onChange={(e) => setCurrentPw(e.target.value)}
              placeholder="Current password"
              autoComplete="current-password"
              className="w-full bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2 text-sm text-cream outline-none focus:border-gold placeholder:text-text-dim"
            />
            <input
              type="password"
              value={newPw}
              onChange={(e) => setNewPw(e.target.value)}
              placeholder="New password (8+ characters)"
              autoComplete="new-password"
              className="w-full bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2 text-sm text-cream outline-none focus:border-gold placeholder:text-text-dim"
            />
            <input
              type="password"
              value={confirmPw}
              onChange={(e) => setConfirmPw(e.target.value)}
              placeholder="Confirm new password"
              autoComplete="new-password"
              className="w-full bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2 text-sm text-cream outline-none focus:border-gold placeholder:text-text-dim"
            />
            <button
              onClick={handleChangePassword}
              disabled={pwSaving || !currentPw || !newPw || !confirmPw}
              className="w-full py-2.5 rounded-xl border border-gold-dim bg-gold-glow text-gold text-sm font-semibold disabled:opacity-50 transition-all hover:brightness-110"
            >
              {pwSaving ? 'Updating...' : 'Update Password'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
